/**
 * Image handling shared by every feature that lets someone attach a photo.
 *
 * `resizeImage` and `convertHeicToPng` were written for the create-ad wizard and
 * lived inside it. The contact form needs the same two things, and so does
 * search-by-image, so they sit here rather than being reimplemented per feature
 * (Vladimir and Evelien, 2026-08-25).
 *
 * Browser-only: these use `Image`, `canvas` and `FileReader`. Call them from
 * event handlers, never from `getStaticProps` or a resolver.
 */
const HEIC_EXT = ".heic";
const PNG_EXT = ".png";
const PNG_MIME = "image/png";
const JPEG_MIME = "image/jpeg";

export type ResizeOptions = {
  minWidth: number;
  maxWidth: number;
  minHeight: number;
  maxHeight: number;
  quality?: number;
};

/**
 * What the create-ad wizard has always used. Kept as the default so lifting the
 * function out of `usePhotoPickerProcessor` changes nothing for that flow.
 */
export const DEFAULT_RESIZE_OPTIONS: ResizeOptions = {
  minWidth: 768,
  maxWidth: 1920,
  minHeight: 768,
  maxHeight: 1920,
  quality: 0.85,
};

/**
 * Progressively smaller attempts for {@link resizeImageToFit}, largest first.
 *
 * A phone photo is 2 to 5 MB, and a single fixed target either wastes quality on
 * small images or still misses the budget on large ones. Stepping down means the
 * common case is handled by the first attempt at full create-ad quality.
 */
const RESIZE_ATTEMPTS: ResizeOptions[] = [
  DEFAULT_RESIZE_OPTIONS,
  {
    minWidth: 640,
    maxWidth: 1600,
    minHeight: 640,
    maxHeight: 1600,
    quality: 0.8,
  },
  {
    minWidth: 512,
    maxWidth: 1280,
    minHeight: 512,
    maxHeight: 1280,
    quality: 0.75,
  },
  {
    minWidth: 448,
    maxWidth: 1024,
    minHeight: 448,
    maxHeight: 1024,
    quality: 0.7,
  },
];

/**
 * Tries to convert an input .heic image into a .png.
 *
 * A file that is not .heic is returned as is.
 *
 * @param file File to convert.
 */
export const convertHeicToPng = async <F extends File>(file: F) => {
  if (!file.name.toLocaleLowerCase().endsWith(HEIC_EXT)) {
    return file;
  }

  const heicLib = await import("heic-to");
  const png = await heicLib.heicTo({ blob: file, type: PNG_MIME });

  return new File([png], file.name.replace(HEIC_EXT, PNG_EXT), {
    type: PNG_MIME,
  }) as F;
};

/**
 * Resizes an image file to fit within the given dimensions while keeping its
 * aspect ratio. Converts to JPEG on a white background, so transparency does not
 * come out black.
 *
 * @param file File to resize.
 * @param options Target box and JPEG quality.
 */
export const resizeImage = (
  file: File,
  options: ResizeOptions = DEFAULT_RESIZE_OPTIONS
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const error = new Error("Unable to resize the image.");

    image.onerror = () => {
      URL.revokeObjectURL(objectURL);
      reject(error);
    };

    image.onload = () => {
      const landscape = {
        width: options.maxWidth,
        height: options.minHeight,
      };

      const portrait = {
        width: options.minWidth,
        height: options.maxHeight,
      };

      let { width, height } = image;
      const ratio = width / height;

      const target = ratio >= 1 ? landscape : portrait;
      const scale = Math.min(target.width / width, target.height / height);

      width = Math.round(width * scale);
      height = Math.round(height * scale);

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const context2d = canvas.getContext("2d");

      if (!context2d) {
        URL.revokeObjectURL(objectURL);
        reject(error);
        return;
      }

      /**
       * Set white background for transparency handling.
       */
      context2d.fillStyle = "#ffffff";
      context2d.fillRect(0, 0, canvas.width, canvas.height);

      context2d.drawImage(image, 0, 0, width, height);

      canvas.toBlob(
        blob => {
          URL.revokeObjectURL(objectURL);

          if (!blob) {
            reject(error);
            return;
          }

          const resizedName = `${file.name}-resized.jpeg`;
          resolve(new File([blob], resizedName, { type: JPEG_MIME }));
        },
        JPEG_MIME,
        options.quality ?? DEFAULT_RESIZE_OPTIONS.quality
      );
    };

    const objectURL = URL.createObjectURL(file);
    image.src = objectURL;
  });
};

/**
 * Shrinks an image until it fits a byte budget, or reports that it cannot.
 *
 * A file already inside the budget is returned untouched, which matters: a PNG
 * screenshot of an order confirmation stays a PNG at full fidelity instead of
 * being re-encoded to JPEG for no reason.
 *
 * @param file File to shrink.
 * @param maxBytes Budget the result has to fit into.
 * @returns The file to send, or `null` when even the smallest attempt is over
 *   budget, so the caller can tell the visitor which photo was left out.
 */
export const resizeImageToFit = async (
  file: File,
  maxBytes: number
): Promise<File | null> => {
  if (file.size <= maxBytes) {
    return file;
  }

  for (const options of RESIZE_ATTEMPTS) {
    const resized = await resizeImage(file, options);

    if (resized.size <= maxBytes) {
      return resized;
    }
  }

  return null;
};
